import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Bookmark, Star, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useFavoriteFilters } from './useFavoriteFilters';

// 🎯 زرار "المفضلة" في السايدبار - بيبقى شغال على أي شاشة ليست تبعتله
// appName بتاعتها. مقفول جوه AppSidebar.js فمالوش وصول مباشر لحالة
// الفلاتر الحالية بتاعة الشاشة (سايدبار وشاشة الليست مش أب/ابن) - فـ
// "تطبيق" مفضلة بيحصل عن طريق نفس أسلوب الأحداث العامة (GLOBAL_...
// CustomEvent) المستخدم في كل زراير السايدبار التانية في نفس الملف ده.
// "حفظ" الفلتر الحالي بيبقى زرار صغير جوه شاشة الليست نفسها (فيها الحالة
// جاهزة أصلاً) - شوف useFavoriteFilters.js
//
// 🎯 اللوحة بتترندر بـ Portal لـ document.body وبموضع fixed محسوب من
// إحداثيات الزرار الفعلية - مش absolute جوه السايدبار. السايدبار نفسه
// عليه overflow-y-auto (نav بيسكرول)، وده بيقفل أي حاجة absolute جواه
// تحاول تطلع بره حدوده حتى لو الاتجاه (RTL/LTR) كان مظبوط. وبما إن
// الشاشة شغالة RTL، الاتجاه اللي "بره" السايدبار بيتغيّر (يمين/شمال)
// فلازم يتحسب من حقيقة الصفحة وقت الفتح مش كلاس ثابت زي left-full
const FavoritesButton = ({ appName, isCollapsed }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';
  const [open, setOpen] = useState(false);
  const [panelPos, setPanelPos] = useState(null);
  const { favorites, loading, deleteFavorite, reload } = useFavoriteFilters(appName);
  const btnRef = useRef(null);
  const panelRef = useRef(null);

  const computePosition = useCallback(() => {
    if (!btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    const panelWidth = 256; // w-64
    // نفتحها ناحية منتصف الشاشة (بعيد عن حافة السايدبار) مهما كان الاتجاه
    const left = isRTL
      ? Math.max(8, rect.left - panelWidth - 6)
      : Math.min(window.innerWidth - panelWidth - 8, rect.right + 6);
    setPanelPos({ top: rect.top, left });
  }, [isRTL]);

  const toggleOpen = () => {
    // 🎯 الزرار ده مركّب مرة واحدة بس (AppSidebar بيتركّب مرة واحدة)، وشاشة
    // الليست بتحفظ المفضلة عن طريق نسخة تانية تمامًا من useFavoriteFilters
    // (جوه WorkOrderList.js نفسها) - يعني مفيش state مشترك بينهم، فالقايمة
    // هنا ما كانتش بتتحدث غير لو عملت Refresh كامل للصفحة. بنجيب القايمة
    // من جديد في كل مرة تتفتح، بدل ما نعتمد على الفيتش اللي حصل مرة واحدة
    // وقت الـ mount
    if (!open) {
      computePosition();
      reload();
    }
    setOpen(o => !o);
  };

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (
        btnRef.current && !btnRef.current.contains(e.target) &&
        panelRef.current && !panelRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', computePosition);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', computePosition);
    };
  }, [open, computePosition]);

  const applyFavorite = (fav) => {
    window.dispatchEvent(new CustomEvent('APPLY_FAVORITE_FILTER', {
      detail: { appName, filters: fav.filters }
    }));
    setOpen(false);
  };

  return (
    <div className="relative">
      <button
        ref={btnRef}
        onClick={toggleOpen}
        title={isCollapsed ? t('sidebar.my_bookmarks', 'My Favorite Filters') : ''}
        className={`w-full text-left px-3 py-2 text-[12px] text-ink-secondary hover:bg-primary hover:text-white flex items-center ${isCollapsed ? 'justify-center' : 'gap-3'} group transition-all rounded-sm`}
      >
        <span className="text-ink-secondary group-hover:text-white transition-colors"><Bookmark size={14} /></span>
        {!isCollapsed && <span className="font-medium truncate">{t('sidebar.my_bookmarks', 'My Favorite Filters')}</span>}
      </button>

      {open && panelPos && createPortal(
        <div
          ref={panelRef}
          style={{ position: 'fixed', top: panelPos.top, left: panelPos.left, width: 256 }}
          className="z-[9999] bg-surface-card border border-line shadow-2xl rounded-control overflow-hidden animate-in fade-in"
        >
          <div className="px-3 py-2 bg-surface-panel border-b border-line text-[10px] font-black uppercase tracking-widest text-ink-secondary">
            {t('favorites.title', 'Favorite Filters')}
          </div>

          <div className="max-h-64 overflow-y-auto">
            {loading && (
              <div className="px-3 py-4 text-[10px] text-ink-secondary italic text-center">...</div>
            )}
            {!loading && favorites.length === 0 && (
              <div className="px-3 py-4 text-[10px] text-ink-secondary italic text-center">
                {t('favorites.empty', 'No favorites saved for this screen yet')}
              </div>
            )}
            {favorites.map(fav => (
              <div
                key={fav.id}
                className="flex items-center justify-between px-3 py-2 border-b border-line-subtle hover:bg-primary-soft group/fav cursor-pointer"
                onClick={() => applyFavorite(fav)}
              >
                <span className="flex items-center gap-2 text-[11px] font-bold text-ink truncate">
                  <Star size={12} className="text-status-warning shrink-0" />
                  {fav.name}
                </span>
                <Trash2
                  size={12}
                  className="text-ink-secondary opacity-0 group-hover/fav:opacity-100 hover:text-status-danger shrink-0 ml-2"
                  onClick={(e) => { e.stopPropagation(); deleteFavorite(fav.id); }}
                />
              </div>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default FavoritesButton;
